import { useState, useEffect, useRef } from 'react';

interface MemoryStats {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
  usagePercent: number;
}

interface UseMemoryMonitorOptions {
  checkInterval?: number;
  warningThreshold?: number;
  criticalThreshold?: number;
  onWarning?: (stats: MemoryStats) => void;
  onCritical?: (stats: MemoryStats) => void;
  enabled?: boolean;
}

export const useMemoryMonitor = (options: UseMemoryMonitorOptions = {}) => {
  const {
    checkInterval = 30000,
    warningThreshold = 0.75,
    criticalThreshold = 0.85,
    onWarning,
    onCritical,
    enabled = true,
  } = options;

  const [memoryStats, setMemoryStats] = useState<MemoryStats | null>(null);
  const lastWarning = useRef(0);
  const lastCritical = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    // Verificar si performance.memory está disponible (Chrome/WebView Android)
    const memory = (performance as any).memory;
    if (!memory) {
      console.warn('⚠️ performance.memory no disponible en este navegador');
      return;
    }

    const checkMemory = () => {
      const usedJSHeapSize = memory.usedJSHeapSize;
      const totalJSHeapSize = memory.totalJSHeapSize;
      const jsHeapSizeLimit = memory.jsHeapSizeLimit;
      const usagePercent = usedJSHeapSize / jsHeapSizeLimit;

      const stats: MemoryStats = {
        usedJSHeapSize,
        totalJSHeapSize,
        jsHeapSizeLimit,
        usagePercent,
      };

      setMemoryStats(stats);

      // Throttle warnings (no más de 1 por minuto)
      const now = Date.now();
      
      if (usagePercent >= criticalThreshold && now - lastCritical.current > 60000) {
        console.error('🔴 MEMORIA CRÍTICA:', Math.round(usagePercent * 100) + '%');
        lastCritical.current = now;
        onCritical?.(stats);
      } else if (usagePercent >= warningThreshold && now - lastWarning.current > 60000) {
        console.warn('⚠️ Memoria alta:', Math.round(usagePercent * 100) + '%');
        lastWarning.current = now;
        onWarning?.(stats);
      }
    };

    checkMemory();
    const interval = setInterval(checkMemory, checkInterval);

    return () => clearInterval(interval);
  }, [enabled, checkInterval, warningThreshold, criticalThreshold, onWarning, onCritical]);

  return memoryStats;
};
